import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** Matched by the print rules in `styles/index.css`. */
const PRINT_ROOT_ID = 'print-root';

export interface PrintDocumentProps {
  /**
   * True to send the document to the printer.
   *
   * It is mounted, printed and torn down again — nothing is ever on screen, so
   * the caller does not have to hide it.
   */
  isOpen: boolean;
  /** Raised once the browser's print dialog has been dealt with. */
  onFinished: () => void;
  /**
   * What the saved file should be called, without an extension.
   *
   * Browsers name a "Save as PDF" after the document's title, so a receipt
   * saved from here lands as `Receipt-2526-0042.pdf` rather than as the name of
   * whatever screen the office happened to be on.
   */
  fileName: string;
  /** True when the paper should be forced to landscape orientation. */
  landscape?: boolean;
  children: ReactNode;
}

/**
 * Prints one document, laid out for paper, from anywhere in the application.
 *
 * The document is portalled into a node appended to `<body>` — a *sibling* of
 * `#root`, not something inside it. That is what lets a receipt be printed from
 * inside an open dialog: the dialog, the rail, the sidebar and the page behind
 * them all live in `#root`, and none of them belong on the paper. The print
 * rules in `styles/index.css` hide everything but this node.
 *
 * There is no PDF library behind it. "Save as PDF" is a destination in every
 * browser's own print dialog, so printing and saving are the same act here —
 * which is also how the student report card has always worked.
 */
export function PrintDocument({ isOpen, onFinished, fileName, landscape = false, children }: PrintDocumentProps) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  /*
    Held in a ref rather than read from props inside the effect below: the
    callback is a fresh closure on every render of the caller, and depending on
    it would tear the document down and print a second copy of it.
  */
  const onFinishedRef = useRef(onFinished);
  useEffect(() => {
    onFinishedRef.current = onFinished;
  });

  // Created only while something is being printed, so the print rules never
  // touch the screens that lay themselves out for paper with `print:` classes.
  useEffect(() => {
    if (!isOpen) return;

    document.documentElement.classList.add('is-printing-document');
    document.body.classList.add('is-printing-document');

    const node = document.createElement('div');
    node.id = PRINT_ROOT_ID;
    document.body.append(node);
    setContainer(node);

    return () => {
      document.documentElement.classList.remove('is-printing-document');
      document.body.classList.remove('is-printing-document');
      node.remove();
      setContainer(null);
    };
  }, [isOpen]);

  // When printing landscape documents (like register exports), force the print
  // page orientation to A4 landscape with optimized margins so columns never clip.
  useEffect(() => {
    if (!isOpen || !landscape) return;

    document.documentElement.classList.add('is-printing-landscape');
    document.body.classList.add('is-printing-landscape');

    const style = document.createElement('style');
    style.id = 'print-document-landscape-style';
    style.textContent = '@media print { @page { size: A4 landscape !important; margin: 6mm 8mm !important; } }';
    document.head.append(style);

    return () => {
      document.documentElement.classList.remove('is-printing-landscape');
      document.body.classList.remove('is-printing-landscape');
      style.remove();
    };
  }, [isOpen, landscape]);

  useEffect(() => {
    if (!container) return;

    document.documentElement.classList.add('is-printing-document');
    document.body.classList.add('is-printing-document');

    /*
      A dialog leaves `overflow: hidden` on the body while it is open, and a
      clipped root cannot be paginated — some browsers stop after the first
      sheet. Cleared for the duration of the print and put back exactly as it
      was, so the dialog underneath is unaffected.
    */
    const heldOverflow = document.body.style.overflow;
    const heldTitle = document.title;
    document.body.style.overflow = '';
    document.title = fileName;

    let hasFinished = false;

    function restore() {
      document.documentElement.classList.remove('is-printing-document');
      document.body.classList.remove('is-printing-document');
      document.body.style.overflow = heldOverflow;
      document.title = heldTitle;
    }

    function finish() {
      if (hasFinished) return;
      hasFinished = true;
      restore();
      onFinishedRef.current();
    }

    window.addEventListener('afterprint', finish);

    // Short tick so the portal is painted and layout classes take effect before print
    const timer = setTimeout(() => {
      window.print();
      finish();
    }, 60);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('afterprint', finish);
      restore();
    };
  }, [container, fileName]);

  return container ? createPortal(children, container) : null;
}
